// FA204 / frank trap — every module-scope mutable + helper below used to be
// referenced bare from the two exports, `setFloatingPanel`/
// `handlePanelDelete` (project_frank_drops_module_local_helpers). This whole
// file coordinates ONE global floating panel singleton (a single DOM
// MutationObserver/ResizeObserver/document click-away listener, the
// re-entrancy `_inFlight`/`_pending*` queue) with no el/root argument stable
// enough to hoist it onto instead (`this` varies per call site) — re-homed
// onto `globalThis.__floatingPanelState`, the pointerCapability.js/
// mwtFlag.js/aiService.js template (the accessor that lazily creates it,
// and every helper that reads/writes it, is now NESTED inside each of the
// two exports below instead of module-scope, so there's no bare same-file
// reference left anywhere).

/**
 * Sets up a floating panel at a given position relative to the component.
 *
 * The floating panel is a component that appears when the user hovers over a
 * component in the DynamicIsland. It displays additional information about the
 * component, such as its props, state, and context.
 *
 * @function setFloatingPanel
 * @param {Object} data The data to display in the floating panel.
 * @param {Object} opts Parameters for the floating panel.
 * @param {Object} opts.panelOffset The position offset of the floating panel
 * @param {Object} opts.customProps Custom Properties for Floating Panel
 */
export async function setFloatingPanel(data, opts = {}) {
  function floatingPanelState() {
    return (globalThis.__floatingPanelState = globalThis.__floatingPanelState || {
      visibilityObserver: null,
      floatingPanelElement: null,
      clickAwayListenerEvent: null,
      cachedOverflow: null,
      resizeObserver: null,
      // Re-entrancy guard. `setFloatingPanel` is async — `await state.set`
      // fires a synchronous DOMQL re-render of the FloatingPanel subtree,
      // which is heavy for colors/themes/icons. Without serialization,
      // rapid clicks on the design-system grid queue dozens of concurrent
      // invocations; each one runs `cleanup()` mid-flight while the prior
      // one is still awaiting state.set, tearing down listeners the next
      // call is about to install and saturating the event loop. The
      // document click-away listener installed by call N is the one that
      // catches click N+1 — so each rapid click closes the panel call N
      // just opened, the visibilityObserver fires cleanup(), and the
      // cycle locks the UI. Tracked under #2064.
      //
      // Serialized so the last-click-wins data is honored (`pendingData`
      // stores the most recent request; once the in-flight call resolves
      // we run one more with the latest data if it changed). This avoids
      // re-entering for every queued click — we collapse N rapid clicks
      // down to "the active one + the last one", regardless of how many
      // the user fires.
      inFlight: null,
      // `null`, not `undefined` — frank's FA201 auto-fix wraps this
      // module-scope mutable state in a `Smut` container that round-trips
      // through JSON at `toJSON` time; `JSON.stringify` drops
      // `undefined`-valued keys entirely, so the emitted Smut container
      // never had a `pendingData` key to write through (frank `to-json`:
      // 14 `unresolvable-scope-read` findings, one per
      // `__scope.Smut._pending*` read/write site — same root class this
      // whole re-home fixes). `null` survives JSON round-tripping and is
      // already the established "no pending" sentinel for the sibling
      // `inFlight` above (never flagged) — same semantics everywhere
      // `pendingData`/`pendingOpts`/`pendingContext` are read as
      // falsy/nullish, so no behavior change.
      pendingData: null,
      pendingOpts: null,
      pendingContext: null,
      panelAuxFn: () => null
    })
  }

  function cleanup(state) {
    state.visibilityObserver?.disconnect?.()
    state.resizeObserver?.disconnect?.()
    if (state.clickAwayListenerEvent) {
      window.document.removeEventListener('click', state.clickAwayListenerEvent, {
        capture: true
      })
    }
    state.floatingPanelElement = null
    state.clickAwayListenerEvent = null
    state.visibilityObserver = null
    state.resizeObserver = null
  }

  function getOverflowElementExists(context, state) {
    if (!state.cachedOverflow) {
      state.cachedOverflow = Boolean(context.getFilesSidebar()?.Overflow?.node)
    }
    return state.cachedOverflow
  }

  // Reference-equality + shallow-key check so rapid clicks on the
  // same color/icon/method don't trigger a redundant state.set.
  // Different keys/types still go through.
  function isSamePayload(a, b) {
    if (a === b) return true
    if (!a || !b) return false
    return a.type === b.type && a.dsType === b.dsType && a.key === b.key
  }

  async function doSetFloatingPanel(data, opts = {}) {
    const state = floatingPanelState()
    const MAX_HEIGHT_MARGIN_NUM = 50
    const FLOATING_PANEL_MAX_HEIGHT = `calc(100dvh - ${MAX_HEIGHT_MARGIN_NUM / 2}px)`

    if (!data || this.call('isEmptyObject', data)) {
      if (state.floatingPanelElement) {
        state.floatingPanelElement?.setNodeStyles?.({
          visibility: 'hidden',
          opacity: '0'
        })
      }
      return
    }

    cleanup(state)
    state.floatingPanelElement = this.getFloatingPanelElement()
    const calleeElement = opts.callee || data?.element || this

    const calleeRect = calleeElement.node?.getBoundingClientRect?.() || {}

    state.clickAwayListenerEvent = (ev) => this.handleClickAway(ev)

    // this is needed to react to external visibility changes of floating panel eg.: handleClickAway placed at Canvas comp.
    state.visibilityObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.target.style?.visibility === 'hidden') {
          cleanup(state)
        }
      }
    })

    state.resizeObserver = new ResizeObserver((entries) => {
      const [floatingPanelEntry] = entries
      if (calleeRect.top < floatingPanelEntry.contentRect.height / 2) {
        state.floatingPanelElement.node.style.transform = `translateY(-${floatingPanelEntry.target.offsetTop - MAX_HEIGHT_MARGIN_NUM / 2}px)`
      }
    })

    state.visibilityObserver.observe(state.floatingPanelElement.node, {
      attributes: true,
      attributeFilter: ['style']
    })

    state.resizeObserver.observe(state.floatingPanelElement.node)

    const calleeLeftPos = calleeRect.left || 0
    const calleeWidth = calleeRect.width || 0
    const floatingPanelLeftPos = calleeLeftPos + calleeWidth

    const updateStyles = (styles) => {
      state.floatingPanelElement?.setNodeStyles?.({
        ...styles
      })

      if (opts.customProps) {
        state.floatingPanelElement.update(opts.customProps)
      }
    }

    const updateSidebarItem = async (calleeItem, isArtboard) => {
      if (isArtboard) {
        await this.call('updateItemInSidebar', calleeItem.type, calleeItem.key, opts)
      } else {
        const type = calleeItem?.type || state.floatingPanelElement.state.type
        await this.call('rerenderFileSidebarByType', type)
      }
    }

    // State management
    const updateSceneState = async (newData, calleeItemState, isArtboard, options = {}) => {
      if (state.floatingPanelElement?.state?.type !== newData?.type) {
        options.preventUpdateAfterCount = 1
      }

      state.floatingPanelElement.state.set(newData, options)

      if (calleeItemState) {
        await updateSidebarItem(calleeItemState, isArtboard)
      }
    }

    const showPanel = () => {
      window.document.addEventListener('click', state.clickAwayListenerEvent, {
        capture: true
      })
      if (!state.floatingPanelElement) {
        return
      }
      state.floatingPanelElement.data.isActive = true

      const allowDropdownOverflow = state.floatingPanelElement.state?.type === 'semantic_color'

      const top = `${calleeRect.top + calleeRect.height}px`

      updateStyles(
        {
          opacity: '1',
          visibility: 'visible',
          position: 'absolute',
          transform: 'translateY(-50%)',
          top,
          overflow: allowDropdownOverflow ? 'visible' : 'auto',
          maxHeight: FLOATING_PANEL_MAX_HEIGHT,
          left: `${floatingPanelLeftPos}px`
        },
        opts
      )
    }

    const overflowExists = getOverflowElementExists(this, state)

    if (!overflowExists || !state.floatingPanelElement) {
      console.warn('Required components not found for FloatingPanel')
      return
    }

    try {
      if (this.node) {
        const calleeItemState =
          // `el.lookup` calls its predicate with ONE argument, so the previous
          // 2-arg `(el, s)` form threw as soon as an ancestor carried both
          // Rename and ContextMenu (the short-circuit hid it everywhere else).
          calleeElement.lookup(
            (ancestor) => ancestor.Rename && ancestor.ContextMenu && ancestor.state?.key
          )?.state || null

        const isArtboard = calleeItemState && this.call('hasArtboard', calleeItemState?.type)
        await updateSceneState(data, calleeItemState, isArtboard)
      }

      showPanel(this)
    } catch (error) {
      console.error('Error setting up floating panel:', error)
    }
  }

  const state = floatingPanelState()

  // Close-intent (no data, empty object) ALWAYS runs synchronously
  // and clears any pending queue + the in-flight marker. This is
  // the user's escape hatch when they hit the X button on the
  // panel after a freeze — without this, a stuck `inFlight` from
  // a degraded prior call would silently drop the close request
  // and leave the palette open (#2071, "color palette stays open").
  const isCloseIntent = !data || this.call('isEmptyObject', data)
  if (isCloseIntent) {
    state.pendingData = null
    state.pendingOpts = null
    state.pendingContext = null
    // Don't await the prior in-flight — it'll resolve into a no-op
    // queue. Just close immediately so the UI is responsive.
    state.inFlight = null
    return doSetFloatingPanel.call(this, data, opts)
  }

  // Re-entrancy coordinator — body lives in doSetFloatingPanel. See
  // the top-of-file comment on `inFlight` for the rationale. Rapid
  // clicks on color/icon/method/theme items used to queue dozens of
  // overlapping invocations that fought over the same observer
  // singletons + document click listener; this collapses them into
  // "currently-running + most-recent-pending" so the UI never gets
  // wedged. (#2064, #2071)
  if (state.inFlight) {
    // If a click is mid-flight, drop the previous pending payload
    // and replace it with the latest user intent. Same-payload
    // rapid clicks become a no-op since `isSamePayload` returns
    // true and we don't queue a redundant repaint.
    if (!isSamePayload(state.pendingData, data)) {
      state.pendingData = data
      state.pendingOpts = opts
      state.pendingContext = this
    }
    return state.inFlight
  }

  state.inFlight = (async () => {
    try {
      await doSetFloatingPanel.call(this, data, opts)
      // After the active call settles, drain ONE queued payload if
      // the user requested something different mid-flight. We don't
      // drain the whole queue — at most one trailing call — because
      // anything else is staler than the latest pending.
      while (state.pendingData !== null) {
        const nextData = state.pendingData
        const nextOpts = state.pendingOpts
        const nextCtx = state.pendingContext
        state.pendingData = null
        state.pendingOpts = null
        state.pendingContext = null
        await doSetFloatingPanel.call(nextCtx, nextData, nextOpts)
      }
    } finally {
      state.inFlight = null
    }
  })()
  return state.inFlight
}

export function handlePanelDelete() {
  // FA204 / frank trap — self-contained inline copy of the state accessor,
  // see setFloatingPanel above. Same globalThis bag, so the listener
  // installed/removed here targets the SAME `panelAuxFn` reference
  // setFloatingPanel's lifecycle relies on.
  function floatingPanelState() {
    return (globalThis.__floatingPanelState = globalThis.__floatingPanelState || {
      visibilityObserver: null,
      floatingPanelElement: null,
      clickAwayListenerEvent: null,
      cachedOverflow: null,
      resizeObserver: null,
      inFlight: null,
      pendingData: null,
      pendingOpts: null,
      pendingContext: null,
      panelAuxFn: () => null
    })
  }
  async function handlePanelKeyUp(event) {
    if (
      event.keyCode !== 46 ||
      !this.state.type ||
      (this.state.type === 'global_theme' &&
        (this.state.key === '@light' || this.state.key === '@dark')) ||
      (this.state.type === 'theme' && this.state.key === 'document')
    ) {
      return
    }

    await this.updateDesignSystem(
      `${(this.state.dsType || this.state.type).toUpperCase()}.${this.state.key}`,
      null
    )
    await this.call('setFloatingPanel')
  }

  const state = floatingPanelState()
  document.body.removeEventListener('keyup', state.panelAuxFn)
  state.panelAuxFn = () => null

  if (!this.call('isEmptyObject', this.state)) {
    state.panelAuxFn = handlePanelKeyUp.bind(this)
    document.body.addEventListener('keyup', state.panelAuxFn)
  }
}
